import {
  Group,
  Box,
  Grid,
  Col,
  useMantineTheme,
  ScrollArea,
  Tooltip,
  Center,
  Anchor,
} from "@mantine/core";
import React, { memo } from "react";
import { useParams } from "react-router";
import Blockies from "react-18-blockies";
import { AmountDisplay } from "../Components/AmountDisplay";
import { TdText, ThText } from "../Components/EpTable";
import { EpTime } from "../Components/EpTime";
import { ROUTER_BASE } from "../Components/Link";
import { EpTxTypeNew } from "../Components/EpTxType";
import { TweenOneGroup } from "rc-tween-one";
import { toChecksumAddress, useIsSmallScreen } from "../Components/Utils";
import { ArbitrageTransactionMessage } from "../Transaction/api/type";
import { ContractDisplay } from "../Components/ContractDisplay";

const columns = 11;

const TransactionDataGridRows = memo(function ({
  data,
  showFullContract,
}: {
  data: Array<ArbitrageTransactionMessage>;
  showFullContract?: boolean;
}) {
  const { chain } = useParams();
  const isSmallScreen = useIsSmallScreen();

  const theme = useMantineTheme();
  const dark = theme.colorScheme === "dark";
  const onEnd = (e: any) => {
    const dom = e.targets;
    dom.style.height = "auto";
  };
  const enterAnim = [
    {
      backgroundColor: dark ? "#25262B" : "#fffeee",
      duration: 0,
    },
    {
      duration: 400,
      backgroundColor: dark ? "rgb(26,27,30)" : theme.white,
      onComplete: onEnd,
    },
  ];

  const leaveAnim = [
    {
      opacity: 0,
      duration: 0,
      onComplete: (e: any) => (e.targets.style.display = "none"),
    },
  ];

  const rows = data.map((row, idx) => {
    const url =
      row.type === "Liquidation"
        ? `/${ROUTER_BASE}/${chain || row.chain}/liquidation/tx/${row.txHash}`
        : `/${ROUTER_BASE}/${chain || row.chain}/tx/${row.txHash}`;
    return (
      <Anchor
        key={`Anchor${row.txHash + row.expiryTimestamp}_${idx}`}
        sx={{
          "&:hover": {
            textDecoration: "none",
          },
        }}
        href={url}
      >
        <Grid
          gutter={0}
          columns={columns}
          key={`${row.txHash + row.expiryTimestamp}_${idx}`}
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: dark
                ? "rgb(44, 46, 51) !important"
                : "#F8F9FA !important",
              textDecoration: "none",
            },
          }}
        >
          <Col
            span={5}
            style={{ borderBottom: "none" }}
            sx={{ padding: "5px 20px" }}
          >
            <Group spacing={10} noWrap align="flex-start">
              <Group
                align="flex-start"
                sx={{ flexDirection: "column" }}
                spacing={0}
              >
                <TdText size="xs" color="#3F4254">
                  #{row.blockNumber}
                </TdText>
                <EpTime timestamp={row.timestamp} dark={dark} />
              </Group>

              <Group
                spacing={6}
                sx={{ marginTop: 6, flexDirection: "column" }}
                position="center"
              >
                <Box
                  sx={{
                    width: 13,
                    height: 13,
                    borderRadius: "50%",
                    background: dark ? "#25262B" : "#FFFFFF",
                    border: "3px solid",
                    borderColor: dark ? "#868E96" : "#CED4DA",
                  }}
                />
                <Box
                  sx={{
                    width: 3,
                    height: 30,
                    background: dark ? "#373A40" : "#E5EAEE",
                    borderRadius: 8,
                  }}
                />
              </Group>
              <EpTxTypeNew
                chain={chain || row.chain}
                tx={row!}
                direction="column"
                withinPortal
              />
            </Group>
          </Col>
          <Col span={isSmallScreen ? 4 : 2} sx={{ padding: "5px 20px" }}>
            <Group
              align="flex-end"
              position={showFullContract ? "left" : "right"}
              sx={{ height: "100%" }}
            >
              {/* <AmountDisplay
              value={((row.revenue - row.cost) / row.cost) * 100}
              suffix="%"
            /> */}
              {showFullContract ? (
                <ContractDisplay
                  address={row.contract as string}
                  // labelList={row.contractLabels}
                  chain={row.chain}
                />
              ) : (
                <Tooltip label={toChecksumAddress(row.contract)}>
                  <Center sx={{ height: 22 }}>
                    <Box
                      sx={{
                        height: 18,
                        width: 18,
                        borderRadius: 4,
                        overflow: "hidden",
                      }}
                    >
                      <Blockies
                        seed={row.contract.toLowerCase()}
                        size={6}
                        scale={3}
                      />
                    </Box>
                  </Center>
                </Tooltip>
              )}
            </Group>
          </Col>
          <Col span={2} sx={{ padding: "5px 20px" }}>
            <Group align="flex-end" position="right" sx={{ height: "100%" }}>
              <AmountDisplay value={row.profit} prefix="$" />
            </Group>
          </Col>
          {isSmallScreen ? null : (
            <Col span={2} sx={{ padding: "5px 20px" }}>
              <Group align="flex-end" position="right" sx={{ height: "100%" }}>
                <AmountDisplay value={row.cost} prefix="$" />
              </Group>
            </Col>
          )}
        </Grid>
      </Anchor>
    );
  });

  return (
    <Box sx={{ overflow: "hidden" }}>
      <TweenOneGroup
        component="div"
        enter={enterAnim as any}
        leave={leaveAnim as any}
        appear={false}
        exclusive
      >
        {rows}
      </TweenOneGroup>
    </Box>
  );
});
export const TransactionDataGrid = memo(function ({
  input,
  contentHeight,
  showFullContract,
}: {
  input: Array<ArbitrageTransactionMessage>;
  contentHeight?: number;
  showFullContract?: boolean;
}) {
  const isSmallScreen = useIsSmallScreen();
  const theme = useMantineTheme();
  const dark = theme.colorScheme === "dark";

  return (
    <ScrollArea
      sx={{
        minWidth: isSmallScreen ? "100%" : 430,
        height: contentHeight || "auto",
        overflowY: "auto",
        position: "relative",
      }}
    >
      <Grid
        gutter={0}
        columns={columns}
        sx={{
          position: "sticky",
          top: -1,
          zIndex: 219,
          background: dark ? "#1A1B1E" : "#FFFFFF",
        }}
      >
        <Col span={5} sx={{ padding: "2px 20px" }}>
          <ThText></ThText>
        </Col>
        <Col span={isSmallScreen ? 4 : 2} sx={{ padding: "2px 20px" }}>
          <Group
            position={showFullContract ? "left" : "right"}
            sx={{ height: "100%" }}
          >
            <ThText>Contract</ThText>
          </Group>
        </Col>
        <Col span={2} sx={{ padding: "2px 20px" }}>
          <Group position="right" sx={{ height: "100%" }}>
            <ThText>Profit</ThText>
          </Group>
        </Col>
        {isSmallScreen ? null : (
          <Col span={2} sx={{ padding: "2px 20px" }}>
            <Group position="right" sx={{ height: "100%" }}>
              <ThText>Cost</ThText>
            </Group>
          </Col>
        )}
      </Grid>
      <TransactionDataGridRows
        data={input}
        showFullContract={showFullContract}
      />
    </ScrollArea>
  );
});
